import React, { useContext, useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import Header from "./common/header/Header";
import { useBodyScrollbarWidth } from "../../hooks/useBodyScrollbarWidth";
import useAphFinanceInit from "../../hooks/useAphFinanceInit";
import { useCarConfigSelector } from "../../redux/store";
import styles from "./Carconfig.module.scss";
import Configure from "./configure/Configure";
import Highlights from "./highlights/Highlights";
import Specs from "./specs/Specs";
import useAphInsuranceInit from "../../hooks/useAphInsuranceInit";
import useAphEtaInit from "../../hooks/useAphEtaInit";
import { getCurrentNavQuerySelector } from "../../utils/selectorUtils";
import TradeInModal from "./common/modals/TradeInModal";
import { useHeaderTrigger } from "../../hooks/useHeaderTrigger";
import { useAemNavOffset } from "../../../../../common-deprecated/hooks";
import NotificationContainer from "./common/notifications/NotificationContainer";
import { triggerHeader } from "../../utils/headerUtils";
import { CarConfigStep } from "../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import "../../../shared-components/css-modules/toyota/styles/globals.scss";
import { getThemeClassname } from "../../../../../common-deprecated/utils";
import { useEcoTable } from "../../hooks/useEcoTable";
import Disclaimers from "./configure/Disclaimers";
import useAphTyCodeInit from "../../hooks/useAphTyCodeInit";
import { ModalContext, useModalMap } from "../../../../../common-deprecated/containers/ModalProvider";
import { aemToyotaAphModalMap } from "./common/modals/modals";
import { MODAL_DEBUG_CAR_INFO, MODAL_DEBUG_FINANCE, MODAL_DEBUG_WINTER_WHEELS } from "../../utils/modalConstants";
import { useCalculateHeaderHeight } from "../../hooks/useCalculateHeaderHeight";
import useStatus from "../../../../../common-deprecated/hooks/useStatus";
import useAphTools from "../../hooks/useAphTools";
import { useAphBaseLayerTracking } from "../../hooks/useAphBaseLayerTracking";
import useDataFetcher from "../../../../../common-deprecated/hooks/useDataFetcher";
import { carConfigFetchDispatcher, getCarConfigComponentFetches } from "../../datafetch/fetchUtils";
import type { GetFetchesFnType } from "../../../../../common-deprecated/utils/registryTypes";
import { COMPONENT_CAR_CONFIG } from "../../../../../shared-logic/server/components";
import { carConfigPostFetch } from "../../datafetch/postFetch";
import { RequestStatusEnum } from "../../../../../common-deprecated/types/CommonTypes";
import LiveRegions from "./common/live-regions/LiveRegions";
import { useZoom } from "../../../../../shared-logic/hooks/useZoom";

const CarConfig = (): JSX.Element => {
    useModalMap(aemToyotaAphModalMap);

    const modelId = useCarConfigSelector((state) => state.car.model.id);
    const settings = useCarConfigSelector((state) => state.commonSettings);
    const activeStep = useCarConfigSelector(getCurrentNavQuerySelector, shallowEqual)?.step;
    const activeMotorization = useCarConfigSelector((state) => state.car.motorization);
    const fetchExternalPrice = useCarConfigSelector(
        (state) => state.externalPrice.state === RequestStatusEnum.Fetching,
    );

    const isAuthor = settings.query.authorPreview || false;
    const apheleiaHeaderRef = useRef<HTMLElement>(null);
    const apheleiaHeaderHeight = useCalculateHeaderHeight(apheleiaHeaderRef);
    const { setStyle } = useContext(ModalContext);
    const headerHeight = useAemNavOffset();
    const scrollbarWidth = useBodyScrollbarWidth();
    const isZoomedIn = useZoom();

    const style = {
        "--or-scrollbar-size": scrollbarWidth,
        "--or-header-height": `${apheleiaHeaderHeight}px`,
        position: "relative",
    } as React.CSSProperties;

    useEffect(() => {
        setStyle(style);
    }, [scrollbarWidth, apheleiaHeaderHeight]);

    useAphBaseLayerTracking();
    useAphFinanceInit();
    useAphInsuranceInit();
    useAphEtaInit();
    useAphTyCodeInit();
    useEcoTable();

    const aphLinks = useAphTools();

    useStatus(settings, settings.component, aphLinks, [
        { key: "f", settings: { type: MODAL_DEBUG_FINANCE } },
        { key: "i", settings: { type: MODAL_DEBUG_CAR_INFO } },
        { key: "w", settings: { type: MODAL_DEBUG_WINTER_WHEELS } },
    ]);

    // Header logic
    useHeaderTrigger(triggerHeader);

    useEffect(() => {
        // Set the height of the header as a Css variable to be used throughout the application.
        document.body.setAttribute("style", `--or-toyota-header-height: ${headerHeight}px`);
    }, [headerHeight]);

    // This causes the component to refetch data when another engine is selected.
    useDataFetcher(
        getCarConfigComponentFetches as unknown as GetFetchesFnType,
        carConfigFetchDispatcher,
        COMPONENT_CAR_CONFIG,
        [activeMotorization, fetchExternalPrice],
        [],
        carConfigPostFetch,
    );

    return (
        <>
            <LiveRegions />
            <NotificationContainer />
            <div
                className={clsx(styles.wrapper, isAuthor && styles.author, getThemeClassname(modelId))}
                style={style}
                data-browser-is-zoomed={isZoomedIn || undefined}
            >
                <div
                    className={styles.loader}
                    style={{
                        background: "#FFF",
                        insetBlock: 0,
                        insetInline: 0,
                        position: "absolute",
                        zIndex: 1000,
                    }}
                />
                <Header activeStep={activeStep as CarConfigStep} ref={apheleiaHeaderRef} />
                {activeStep === CarConfigStep.Configure && <Configure />}
                {activeStep === CarConfigStep.Highlights && <Highlights />}
                {activeStep === CarConfigStep.Specs && <Specs />}

                <Disclaimers />
                <TradeInModal />
            </div>
        </>
    );
};

export default CarConfig;
